/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Avatar,
  DetailItemList,
  ICON_BRIEFCASE_OUTLINE,
  ICON_EMAIL_OUTLINE,
  ICON_PHONE_OUTLINE,
  Message,
  Popover,
  PopoverProps,
  Spacing,
  Stack,
  Text,
} from '@doveit/bricks';
import PopoverPreview from '../../../components/popover-preview/PopoverPreview';
import PopoverPreviewSkeleton from '../../../components/popover-preview/PopoverPreview.skeleton';
import { useApplicant } from '../../../applicant/hooks/use-applicant/useApplicant';
import UserActions from '../../../components/user-actions/UserActions';
import { ThemeIcons } from '../../../theme/icons';
import * as styles from './ViewApplicantPopoverPreview.style';
import { Applicant } from '../../../providers/api/dtos';

interface WithApplicant {
  applicant: Applicant,
  applicantId?: never,
}

interface WithApplicantId {
  applicantId: number,
  applicant?: never,
}

interface BaseViewApplicantPopoverPreviewProps extends React.AriaAttributes {
  trigger: PopoverProps['trigger'],
  isFranchising?: boolean,
}

export type ViewApplicantPopoverPreviewProps = BaseViewApplicantPopoverPreviewProps & (WithApplicant | WithApplicantId);

export const LOAD_APPLICANT_ERROR_MESSAGE = 'Non è stato possibile caricare i dati del candidato';

const ViewApplicantPopoverPreview: React.FC<ViewApplicantPopoverPreviewProps> = ({
  applicantId,
  applicant: externalApplicant,
  trigger,
  isFranchising,
  ...rest
}) => {
  const {
    data: loadedApplicant,
    isLoading: isApplicantLoading,
    error: applicantError,
  } = useApplicant(applicantId);

  const applicant = externalApplicant || loadedApplicant;

  return (
    <Popover
      aria-label="Anteprima candidato"
      trigger={trigger}
      hasArrow
      {...rest}
    >
      {/*
        * tabIndex={0} makes this div the first tabbable element inside the popover.
        * The Popover component wraps its content in FloatingFocusManager (@floating-ui/react),
        * which on open auto-focuses the first tabbable element (initialFocus defaults to 0).
        * Without this, FloatingFocusManager focuses the first ActionIcon in UserActions,
        * whose Tooltip listens to useFocus() and opens immediately — showing an unwanted tooltip on popover open.
        * By making this wrapper div tabbable first, the auto-focus lands here (a non-interactive element)
        * and the ActionIcon tooltips are not triggered.
        */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div css={styles.wrapper} tabIndex={0}>
        {(isApplicantLoading && !applicantError) && (
          <PopoverPreviewSkeleton />
        )}

        {(applicantError) && (
          <Spacing padding={200}>
            <Message
              type="critical"
              message={LOAD_APPLICANT_ERROR_MESSAGE}
            />
          </Spacing>
        )}

        {applicant && (
          <PopoverPreview
            title={applicant.name}
            details={`Candidato${isFranchising ? ' franchising' : ''}`}
            avatar={(
              <Avatar
                placeholder={ICON_BRIEFCASE_OUTLINE}
                size="L"
                icon={!isFranchising ? ThemeIcons.DOVE : undefined}
              />
            )}
          >
            <Stack gap={150}>
              <DetailItemList>
                {applicant.phoneNumber && (
                  <DetailItemList.Item icon={ICON_PHONE_OUTLINE}>
                    <Text.Body>{applicant.phoneNumber}</Text.Body>
                  </DetailItemList.Item>
                )}
                {applicant.email && (
                  <DetailItemList.Item icon={ICON_EMAIL_OUTLINE}>
                    <Text.Body>{applicant.email}</Text.Body>
                  </DetailItemList.Item>
                )}
              </DetailItemList>
              <UserActions
                user={applicant}
                hideLinkedInAction={false}
              />
            </Stack>
          </PopoverPreview>
        )}
      </div>
    </Popover>
  );
};

export default ViewApplicantPopoverPreview;
